<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { get } from 'svelte/store';
    import { filters, resetFilters, type ContactStatusFilter, type UsualVoteMethod } from '$lib/filters';
    import { debounce } from '$lib/debounce';
    import { getVoterFacets } from '$lib/api';

    export let open = false;

    const dispatch = createEventDispatcher<{ apply: void; close: void }>();

    // Local draft for the search box. routes/Map.svelte reactively reloads
    // voters (a server request) on every filters-store change, so committing
    // each keystroke would fire one request per character. Debounce the commit
    // to the store; Apply flushes any pending value immediately.
    let searchDraft = get(filters).search;
    const commitSearch = debounce((value: string) => {
        filters.update(f => (f.search === value ? f : { ...f, search: value }));
    }, 350);

    // When the store's search is cleared elsewhere (e.g. Reset), reflect that
    // in the local draft so the input box empties too.
    $: if ($filters.search === '' && searchDraft !== '') searchDraft = '';

    function onSearchInput(e: Event) {
        searchDraft = (e.target as HTMLInputElement).value;
        commitSearch(searchDraft);
    }

    const STATUSES: { value: ContactStatusFilter; label: string }[] = [
        { value: 'never_contacted', label: 'Never contacted' },
        { value: 'not_home',        label: 'Not home' },
        { value: 'refused',         label: 'Refused' },
        { value: 'spoke',           label: 'Spoke' },
    ];

    const PARTIES = ['DEM', 'REP', 'UNA', 'LIB', 'GRE'];

    const GENDERS = [
        { value: 'F', label: 'Female' },
        { value: 'M', label: 'Male' },
        { value: 'U', label: 'Unspecified' },
    ];
    const VOTE_METHODS: { value: UsualVoteMethod; label: string }[] = [
        { value: 'mail', label: 'Mail' },
        { value: 'early', label: 'Early' },
        { value: 'election_day', label: 'Election Day' },
    ];

    // Precinct facets: loading / loaded / error, rather than inferring
    // "loading" from an empty array (which also reads as "failed" and
    // "genuinely no precincts" and never resolves out of it on failure).
    type FacetsState = 'loading' | 'loaded' | 'error';
    let facetsState: FacetsState = 'loading';
    let precinctOptions: string[] = [];

    async function loadFacets() {
        facetsState = 'loading';
        try {
            // NOTE: always fetches the 'targeted' audience's precinct list
            // (the server default). An admin who has switched Map.svelte's
            // audience toggle to "All HD-51" can see a precinct list
            // narrower than the voters on screen. `audience` lives in
            // Map.svelte's local state and isn't passed into this drawer;
            // threading it through would mean changing Map.svelte, which is
            // outside this fix's file scope. Left as a known limitation.
            const f = await getVoterFacets();
            precinctOptions = f.precincts;
            facetsState = 'loaded';
        } catch {
            facetsState = 'error';
        }
    }

    // Fetch once per drawer-open, cached for the life of the component once
    // it succeeds. This reacts to the TRANSITION into `open` - guarded by
    // `hasRequestedFacets`, a flag this block itself sets synchronously -
    // rather than to `facetsState`, which the async load mutates.
    //
    // The previous version guarded on a flag that the fetch's `.catch`
    // itself reset. Svelte re-runs a `$:` block whenever any variable it
    // reads is reassigned anywhere, including from inside a `.then`/`.catch`
    // callback - so the instant the rejection landed, this block re-ran with
    // `open` still true, refetched immediately, and a fast-failing request
    // (offline, 401, 500) became an unbounded request loop. Verified against
    // Svelte's actual behaviour (see the `searchDraft` guard above, which
    // uses the same "read the flag you just set" pattern safely because the
    // written value makes its own guard condition false).
    //
    // Net behaviour:
    //   - success: cached forever; later opens never refetch.
    //   - failure: retried at most once, on the NEXT open - never while the
    //     drawer stays open (`hasRequestedFacets` only clears once `open`
    //     goes false, and only when the last attempt errored).
    let hasRequestedFacets = false;
    $: if (open && !hasRequestedFacets) {
        hasRequestedFacets = true;
        void loadFacets();
    } else if (!open && hasRequestedFacets && facetsState === 'error') {
        hasRequestedFacets = false;
    }

    const HISTORY_OPTIONS = [
        { value: '',        label: 'All voters' },
        { value: 'g24',     label: 'G24 voters' },
        { value: 'p26',     label: 'P26 voters' },
        { value: 'dropoff', label: 'Drop-off (voted G22, not G24)' },
    ];

    const PETITION_OPTIONS = [
        { value: '',           label: 'Any' },
        { value: 'signed',     label: 'Signed' },
        { value: 'not_signed', label: 'Not signed' },
    ];

    const VOTED_OPTIONS = [
        { value: '',          label: 'Any' },
        { value: 'voted',     label: 'Already voted' },
        { value: 'not_voted', label: 'Not yet voted' },
    ];

    const TEXT_OUTREACH_OPTIONS = [
        { value: '',              label: 'Any' },
        { value: 'opted_in',      label: 'Opted in' },
        { value: 'opted_out',     label: 'Opted out' },
        { value: 'no_response',   label: 'No response' },
        { value: 'not_contacted', label: 'Not contacted' },
        { value: 'none',          label: 'No text data' },
    ];

    const SUPPORT_PRESETS = [
        { label: 'Supporters',      min: 4,    max: 5    },
        { label: 'Undecided',       min: 3,    max: 3    },
        { label: 'Opposed',         min: 1,    max: 2    },
        { label: 'Never contacted', min: null, max: null, status: 'never_contacted' as ContactStatusFilter },
    ];

    function toggleStatus(v: ContactStatusFilter) {
        filters.update(f => ({
            ...f,
            status: f.status.includes(v) ? f.status.filter(s => s !== v) : [...f.status, v],
        }));
    }

    function toggleParty(p: string) {
        filters.update(f => ({
            ...f,
            party: f.party.includes(p) ? f.party.filter(x => x !== p) : [...f.party, p],
        }));
    }

    function toggleIn(key: 'gender' | 'precinct' | 'usual_vote_method', v: string) {
        filters.update(f => {
            const cur = f[key] as string[];
            const next = cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v];
            return { ...f, [key]: next };
        });
    }

    function applyPreset(p: typeof SUPPORT_PRESETS[0]) {
        filters.update(f => ({
            ...f,
            support_min: p.min,
            support_max: p.max,
            status: 'status' in p && p.status ? [p.status] : f.status,
        }));
    }

    function handleReset() {
        resetFilters();
    }

    function handleApply() {
        // Flush any pending debounced search edit so the just-typed value is in
        // the store before the parent reloads voters.
        commitSearch.flush();
        dispatch('apply');
    }

    function handleClose() {
        dispatch('close');
    }
</script>

<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<div
    role="dialog"
    aria-label="Filters"
    aria-modal="true"
    class="drawer"
    class:open
    on:keydown={e => e.key === 'Escape' && handleClose()}
>
    <div class="drawer-header">
        <h2>Filters</h2>
        <button class="icon-btn" aria-label="Close filters" on:click={handleClose}>✕</button>
    </div>

    <div class="drawer-body">
        <!-- Search -->
        <label class="field-label">
            Search
            <input
                type="text"
                placeholder="Name or address"
                value={searchDraft}
                on:input={onSearchInput}
            />
        </label>

        <!-- Status -->
        <fieldset>
            <legend>Contact status</legend>
            {#each STATUSES as s}
                <label class="chip-label">
                    <input
                        type="checkbox"
                        checked={$filters.status.includes(s.value)}
                        on:change={() => toggleStatus(s.value)}
                    />
                    {s.label}
                </label>
            {/each}
        </fieldset>

        <!-- Support level -->
        <fieldset>
            <legend>Support level</legend>
            <div class="range-row">
                <label>
                    Min
                    <input type="number" min="1" max="5" bind:value={$filters.support_min} />
                </label>
                <span>–</span>
                <label>
                    Max
                    <input type="number" min="1" max="5" bind:value={$filters.support_max} />
                </label>
            </div>
            <div class="preset-row">
                {#each SUPPORT_PRESETS as preset}
                    <button class="preset-btn" on:click={() => applyPreset(preset)}>
                        {preset.label}
                    </button>
                {/each}
            </div>
        </fieldset>

        <!-- County -->
        <label class="field-label">
            County
            <input type="text" aria-label="County" bind:value={$filters.county} placeholder="e.g. Lee" />
        </label>

        <!-- Party -->
        <fieldset>
            <legend>Party</legend>
            {#each PARTIES as p}
                <label class="chip-label">
                    <input
                        type="checkbox"
                        checked={$filters.party.includes(p)}
                        on:change={() => toggleParty(p)}
                    />
                    {p}
                </label>
            {/each}
        </fieldset>

        <!-- Gender -->
        <fieldset>
            <legend>Gender</legend>
            {#each GENDERS as g}
                <label class="chip-label">
                    <input
                        type="checkbox"
                        checked={$filters.gender.includes(g.value)}
                        on:change={() => toggleIn('gender', g.value)}
                    />
                    {g.label}
                </label>
            {/each}
        </fieldset>

        <!-- Usually votes -->
        <fieldset>
            <legend>Usually votes</legend>
            {#each VOTE_METHODS as m}
                <label class="chip-label">
                    <input
                        type="checkbox"
                        checked={$filters.usual_vote_method.includes(m.value)}
                        on:change={() => toggleIn('usual_vote_method', m.value)}
                    />
                    {m.label}
                </label>
            {/each}
        </fieldset>

        <!-- Precinct -->
        <fieldset>
            <legend>Precinct</legend>
            {#if facetsState === 'loading'}
                <p class="hint" role="status">Loading precincts...</p>
            {:else if facetsState === 'error'}
                <p class="hint hint-error" role="status">
                    Could not load precincts. Close and reopen filters to try again.
                </p>
            {:else if precinctOptions.length === 0}
                <p class="hint" role="status">No precincts available.</p>
            {:else}
                {#each precinctOptions as pr}
                    <label class="chip-label">
                        <input
                            type="checkbox"
                            checked={$filters.precinct.includes(pr)}
                            on:change={() => toggleIn('precinct', pr)}
                        />
                        {pr}
                    </label>
                {/each}
            {/if}
        </fieldset>

        <!-- Age range -->
        <fieldset>
            <legend>Age range</legend>
            <div class="range-row">
                <label>
                    Min
                    <input type="number" min="18" max="120" bind:value={$filters.age_min} />
                </label>
                <span>–</span>
                <label>
                    Max
                    <input type="number" min="18" max="120" bind:value={$filters.age_max} />
                </label>
            </div>
        </fieldset>

        <!-- Voter history -->
        <label class="field-label">
            Voter history
            <select bind:value={$filters.voter_history}>
                {#each HISTORY_OPTIONS as opt}
                    <option value={opt.value}>{opt.label}</option>
                {/each}
            </select>
        </label>

        <!-- Petition -->
        <label class="field-label">
            Petition
            <select bind:value={$filters.petition}>
                {#each PETITION_OPTIONS as opt}
                    <option value={opt.value}>{opt.label}</option>
                {/each}
            </select>
        </label>

        <!-- Early vote / absentee (NCSBE daily file) -->
        <label class="field-label">
            Voted (2026 general)
            <select bind:value={$filters.voted}>
                {#each VOTED_OPTIONS as opt}
                    <option value={opt.value}>{opt.label}</option>
                {/each}
            </select>
        </label>

        <!-- Consultant text outreach (SMS file) -->
        <label class="field-label">
            Text status
            <select bind:value={$filters.text_outreach}>
                {#each TEXT_OUTREACH_OPTIONS as opt}
                    <option value={opt.value}>{opt.label}</option>
                {/each}
            </select>
        </label>

        <!-- Toggles -->
        <label class="toggle-label">
            <input type="checkbox" bind:checked={$filters.show_dnc} />
            Show DNC voters
        </label>

        <label class="toggle-label">
            <input
                type="checkbox"
                aria-label="My contacts only"
                bind:checked={$filters.my_contacts_only}
            />
            My contacts only
        </label>

        <label class="toggle-label">
            <input type="checkbox" bind:checked={$filters.has_callback} />
            Has callback scheduled
        </label>

        <label class="toggle-label">
            <input type="checkbox" bind:checked={$filters.household_partly_voted} />
            Households where some but not all have voted
        </label>
    </div>

    <div class="drawer-footer">
        <button class="btn-secondary" on:click={handleReset}>Reset</button>
        <button class="btn-primary" on:click={handleApply}>Apply</button>
    </div>
</div>

{#if open}
    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
    <div class="overlay" on:click={handleClose}></div>
{/if}

<style>
    .drawer {
        position: fixed;
        top: 0; right: 0; bottom: 0;
        width: min(380px, 100vw);
        background: white;
        box-shadow: -4px 0 16px rgba(0,0,0,.2);
        transform: translateX(100%);
        transition: transform 0.25s ease;
        display: flex; flex-direction: column;
        z-index: 300;
        overflow: hidden;
    }
    .drawer.open { transform: translateX(0); }
    .drawer-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 16px; border-bottom: 1px solid #e5e7eb;
    }
    .drawer-header h2 { margin: 0; font-size: 1.1rem; }
    .drawer-body { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 16px; }
    .drawer-footer { padding: 16px; border-top: 1px solid #e5e7eb; display: flex; gap: 8px; }
    .field-label { display: flex; flex-direction: column; gap: 4px; font-size: .875rem; }
    .field-label input, .field-label select { padding: 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 1rem; }
    fieldset { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; }
    legend { font-size: .875rem; font-weight: 600; padding: 0 4px; }
    .chip-label { display: inline-flex; align-items: center; gap: 4px; margin: 4px; cursor: pointer; }
    .hint { margin: 4px; font-size: .8rem; color: #6b7280; }
    .hint-error { color: #b91c1c; }
    .toggle-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: .875rem; }
    .range-row { display: flex; align-items: center; gap: 8px; }
    .range-row label { display: flex; flex-direction: column; gap: 2px; font-size: .75rem; }
    .range-row input { width: 60px; padding: 4px 6px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 1rem; }
    .preset-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .preset-btn { padding: 4px 10px; border: 1px solid #d1d5db; border-radius: 16px; background: white; cursor: pointer; font-size: .8rem; }
    .preset-btn:hover { background: #f3f4f6; }
    .btn-primary { flex: 1; padding: 12px; background: #1d4ed8; color: white; border: none; border-radius: 8px; font-size: 1rem; cursor: pointer; }
    .btn-secondary { flex: 1; padding: 12px; background: white; color: #374151; border: 1px solid #d1d5db; border-radius: 8px; font-size: 1rem; cursor: pointer; }
    .overlay { position: fixed; inset: 0; background: rgba(0,0,0,.3); z-index: 299; }
    .icon-btn { background: none; border: none; cursor: pointer; font-size: 1.2rem; padding: 4px; }
</style>
